import { useTranslation } from 'react-i18next'

import { formatUsd } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import type { MarketRateStats } from '../model/types'

interface MarketRateSummaryProps {
  stats?: MarketRateStats
}

function Stat({
  label,
  value,
  perMi,
  emphasized,
}: {
  label: string
  value: number
  perMi?: number
  emphasized?: boolean
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col items-start gap-0.75">
      <span className="text-2xs font-bold tracking-wide text-gray-300 uppercase">
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        <span
          className={cn(
            'font-bold',
            emphasized ? 'text-2xl text-primary' : 'text-xl text-gray-900',
          )}
        >
          {formatUsd(value)}
        </span>
        {perMi !== undefined && (
          <span className="text-xs text-gray-500">
            {t('table.marketRate.perMi', { rate: perMi.toFixed(2) })}
          </span>
        )}
      </div>
    </div>
  )
}

function SampleHint({
  sampleSize,
  referenceDistanceMi,
}: {
  sampleSize?: number
  referenceDistanceMi?: number
}) {
  const { t } = useTranslation()
  if (sampleSize === undefined && referenceDistanceMi === undefined) return null

  return (
    <div className="flex flex-col items-end gap-0.5 text-2xs text-gray-500">
      {sampleSize !== undefined && (
        <span>{t('table.marketRate.sampleSize', { count: sampleSize })}</span>
      )}
      {referenceDistanceMi !== undefined && (
        <span>
          {t('table.marketRate.referenceDistance', {
            miles: referenceDistanceMi,
          })}
        </span>
      )}
    </div>
  )
}

export function MarketRateSummary({ stats }: MarketRateSummaryProps) {
  const { t } = useTranslation()

  if (!stats) {
    return (
      <div className="flex items-center border-b border-gray-200 bg-blue-50 px-5 py-4">
        <span className="text-xs text-gray-500">
          {t('table.marketRate.empty')}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between border-b border-gray-200 bg-blue-50 px-5 py-4">
      <div className="flex items-center gap-6">
        <Stat
          label={t('table.marketRate.low')}
          value={stats.low}
          perMi={stats.lowPerMi}
        />
        <span className="h-10 w-px bg-blue-100" aria-hidden />
        <Stat
          label={t('table.marketRate.recommended')}
          value={stats.recommended}
          perMi={stats.recommendedPerMi}
          emphasized
        />
        <span className="h-10 w-px bg-blue-100" aria-hidden />
        <Stat
          label={t('table.marketRate.high')}
          value={stats.high}
          perMi={stats.highPerMi}
        />
      </div>
      <SampleHint
        sampleSize={stats.sampleSize}
        referenceDistanceMi={stats.referenceDistanceMi}
      />
    </div>
  )
}
